// Faza 6.2.23 (2026-05-03): split z customers.ts — see .plans/audit-customers-split.md
// Sesja 3.5 (2026-05-03): migracja na Nanostores dla customers state

import {
  $customersList,
  $customersLoading,
  $customersPage,
  $customersFilterType,
  $customersFilterStatus,
  $customersFilterCountry,
} from '../../stores/customers.js';
import type { CustomerRow, ApiError } from '../../types/dto.ts';
// 0238: tagi klientów — chipy w wierszach + pasek filtra nad tabelą.
import { chipyTagowKlienta, pasekTagowKlientow } from '../customer-tags.ts';

// `state` (proxy do window.__sbState) zostaje dla pól nie-zmigrowanych:
// customersHasMore, customersTotal, customersFiltersKey,
// customersAggregatedCounts, customersAggregatedCountsKey.
var state: any = (typeof window !== 'undefined' && window.__sbState) || (function() {
  var _p = new Proxy({}, {
    get: function(_, k: any) { return window.__sbState ? window.__sbState[k] : undefined; },
    set: function(_, k: any, v) { if (window.__sbState) window.__sbState[k] = v; return true; },
    has: function(_, k: any) { return window.__sbState ? (k in window.__sbState) : false; },
  });
  return _p;
})();


// Lazy delegates (Faza 6.2.23 — cross-module deps)
function _vatBadgeHtml(this: any, ...args: any[]): any { return ((window.__sbExports && window.__sbExports._vatBadgeHtml) || window._vatBadgeHtml).apply(this, args); }

// Cross-file delegates (Faza 6.2.23 — między customers/* files)
function openCustomerDrawer(this: any, ...args: any[]): any { return ((window.__sbExports && window.__sbExports.openCustomerDrawer) || window.openCustomerDrawer).apply(this, args); }
// 2026-06-02: numerowana paginacja (współdzielony komponent z pagination.ts, jak w zamówieniach)
function renderPagination(this: any, ...args: any[]): any { return ((window.__sbExports && (window.__sbExports as any).renderPagination) || (window as any).renderPagination).apply(this, args); }


function countryToFlag(code: string | null | undefined) {
    if (!code || code.length !== 2) return '';
    var c = code.toLowerCase();
    return '<img src="https://flagcdn.com/16x12/' + c + '.png" alt="' + c.toUpperCase() + '" ' +
      'style="width:16px;height:12px;vertical-align:middle;border-radius:2px;flex-shrink:0" ' +
      'onerror="this.style.display=\'none\'">';
}

function groupCustomersByDupes(list: CustomerRow[]) {
    var assigned: Record<string, boolean> = {}, groups: CustomerRow[][] = [];
    list.forEach(function(c: CustomerRow) {
      if (assigned[c.id]) return;
      var grp = [c];
      assigned[c.id] = true;
      list.forEach(function(o: CustomerRow) {
        if (assigned[o.id] || o.id === c.id) return;
        var sameLogin = c.login && o.login && c.login.toLowerCase() === o.login.toLowerCase();
        var samePerson = c.phone && o.phone && c.phone === o.phone &&
                         c.first_name && o.first_name && c.first_name.toLowerCase() === o.first_name.toLowerCase() &&
                         c.last_name && o.last_name && c.last_name.toLowerCase() === o.last_name.toLowerCase();
        if (sameLogin || samePerson) { grp.push(o); assigned[o.id] = true; }
      });
      grp.sort(function(a, b) { return a.id < b.id ? -1 : 1; }); // primary = najstarszy
      groups.push(grp);
    });
    return groups;
}

// AUDYT 2026-08-11 (pakiet klienci): po mutacji klienta stygły DWA cache —
// liczniki sidebar (state.customersAggregatedCounts, „raz na sesję") i auto-cache
// GET /api/customers (TTL 30 s). Lista i liczniki pokazywały stan sprzed zmiany do F5.
function odswiezKlientowPoMutacji() {
  try {
    state.customersAggregatedCounts = null;
    state.customersAggregatedCountsKey = null;
  } catch (_e) {}
  try {
    var ac = (window as any).__sbApiCache;
    if (ac && typeof ac.invalidateDomain === 'function') ac.invalidateDomain('customers');
  } catch (_e) {}
  // S-139: NIE zerujemy $customersList tutaj — w trybie cichym (renderCustomers({cicho:true}))
  // tabela ma już wiersze i jest podmieniana w jednym kroku po odpowiedzi; zerowanie tu wygasiłoby
  // ją na mgnienie „Ładowanie klientów..." przed refetchem (tego właśnie unikamy).
  // Przerysuj tylko gdy lista klientów faktycznie jest na ekranie (mutacje idą też z modala/szuflady)
  if (document.getElementById('customers-table-body')) renderCustomers({ cicho: true });
}
if (typeof window !== 'undefined') (window as any).__sbOdswiezKlientow = odswiezKlientowPoMutacji;

async function renderCustomers(opts: { cicho?: boolean } = {}) {
    var container = document.getElementById('customers-table-body');
    if (!container) return;
    // S-139: tryb cichy (odświeżenie po mutacji klienta, nie pierwsze wejście/zmiana filtra) —
    // gdy tabela ma już wiersze, nie wpisujemy „Ładowanie klientów...”. Finalna podmiana (:297ish)
    // idzie w jednym kroku, więc tabela się nie zapada i scroll strony przeżywa.
    var _cicho = !!opts.cicho && !!container.querySelector('tr[data-customer-id]');
    if (!_cicho) {
      container.innerHTML = '<tr><td colspan="10" style="text-align:center;padding:24px;color:var(--color-text-muted)">Ładowanie klientów...</td></tr>';
    }

    // Get filter values from state (sidebar) + search from DOM
    var searchEl = document.getElementById('customer-search');
    var filterType = $customersFilterType.get() || 'all';
    var filterStatus = $customersFilterStatus.get() || 'all';
    var filterCountry = $customersFilterCountry.get() || 'all';
    // 0238: filtr po tagu klienta (chip na pasku nad tabelą; 0 = brak filtra).
    var filterTag = Number(state.customersFilterTag || 0);
    var searchQuery = (searchEl ? (searchEl as any).value : '').trim();

    // Phase 1.2 (2026-04-29 perf-overhaul): pagination zamiast page_size=10000.
    // Reset state przy każdym filter change (przez `state.customersFiltersKey`).
    var filtersKey = JSON.stringify({s: searchQuery, t: filterType, st: filterStatus, c: filterCountry, tg: filterTag});
    // 2026-06-02: numerowana paginacja (jak w zamówieniach). Strona z `state.currentPage.customers`
    // (ustawiane przez pagination._bindPaginationEvents), per-page z `state.itemsPerPage`.
    state.currentPage = state.currentPage || {};
    if (state.customersFiltersKey !== filtersKey) {
      state.customersFiltersKey = filtersKey;
      $customersList.set([]);
      $customersPage.set(1);
      state.currentPage.customers = 1;   // reset na 1 przy zmianie filtra/szukajki
      state.customersTotal = 0;
    }
    var PAGE_SIZE = (state.itemsPerPage && Number(state.itemsPerPage)) || 50;
    var currentPage = state.currentPage.customers || $customersPage.get() || 1;

    try {
      $customersLoading.set(true);
      // Phase 1.3 (perf-overhaul, 2026-04-29): używamy SellBaseAPICached dla auto-cache + dedupe.
      // 30s TTL → drugie wejście w zakładkę instant. POST/PUT/DELETE invaliduje automatycznie.
      var _api = (window.SellBaseAPICached && window.SellBaseAPICached.getCustomers) || SellBaseAPI.getCustomers;
      // KRYT (review 0238): `tag: ''` leciałoby jako ?tag= do parametru INT
      // (getCustomers serializuje wszystkie klucze) → 422 na KAŻDYM wejściu.
      // Klucz dodajemy wyłącznie przy realnym filtrze.
      var _params: any = {
        page: currentPage,
        page_size: PAGE_SIZE,
        search: searchQuery || '',
        status: filterStatus !== 'all' ? filterStatus : '',
        type: filterType !== 'all' ? filterType : '',
        country: filterCountry !== 'all' ? filterCountry : ''
      };
      if (filterTag > 0) _params.tag = filterTag;
      var resp = await _api(_params);
      var newItems = Array.isArray(resp && resp.items) ? resp.items : [];
      // ZAWSZE replace (nie append) — każda strona to świeży fetch (numerowana paginacja).
      $customersList.set(newItems);
      $customersPage.set(currentPage);   // sync legacy store
      state.customersTotal = (resp && typeof resp.total === 'number') ? resp.total : newItems.length;
    } catch (e) {
      $customersLoading.set(false);
      // S-139: cichy refetch (po mutacji) — błąd zostawia starą tabelę w spokoju, bez czerwonego komunikatu.
      if (_cicho) { console.warn('[customers] cichy refetch nieudany:', e); return; }
      container.innerHTML = '<tr><td colspan="10" style="text-align:center;padding:24px;color:var(--color-error)">Nie udało się pobrać klientów: ' + escapeHtml((e as any)?.message || 'Błąd API') + '</td></tr>';
      return;
    }
    $customersLoading.set(false);

    // Sidebar badge counts — pobierz z lekkiego endpointu /api/customers/aggregated-counts
    // (jeden GROUP BY zamiast łączenia tysięcy wierszy po stronie klienta).
    // Tylko raz na sesję — counts cachujemy w state. Invalidacja po POST/PUT/DELETE customer.
    if (!state.customersAggregatedCounts || state.customersFiltersKey !== state.customersAggregatedCountsKey) {
      try {
        var countsResp = await fetch('/api/customers/aggregated-counts', {
          headers: { 'Authorization': 'Bearer ' + (SellBaseAPI.getAuthToken ? SellBaseAPI.getAuthToken() : (localStorage.getItem('orderflow_token') || sessionStorage.getItem('orderflow_token') || '')) }
        }).then(function(r) { return r.json(); });
        state.customersAggregatedCounts = countsResp || {};
        state.customersAggregatedCountsKey = filtersKey;
      } catch(_) { state.customersAggregatedCounts = state.customersAggregatedCounts || {}; }
    }
    var aggCounts = state.customersAggregatedCounts || {};
    var typeCounts = {
      all: aggCounts.total || 0,
      company: (aggCounts.by_type || {}).company || 0,
      individual: (aggCounts.by_type || {}).individual || 0,
    };
    var statusCounts = {
      all: aggCounts.total || 0,
      standard: (aggCounts.by_status || {}).standard || 0,
      staly: (aggCounts.by_status || {}).staly || 0,
      hurt: (aggCounts.by_status || {}).hurt || 0,
      vip: (aggCounts.by_status || {}).vip || 0,
      czarna_lista: (aggCounts.by_status || {}).czarna_lista || 0,
    };
    ['all', 'company', 'individual'].forEach(function(k) {
      var el = document.getElementById('ctype-' + k + '-count');
      if (el) el.textContent = (typeCounts as any)[k] || '';
    });
    ['all', 'standard', 'staly', 'hurt', 'vip'].forEach(function(k) {
      var el = document.getElementById('cstatus-' + k + '-count');
      if (el) el.textContent = (statusCounts as any)[k] || '';
    });
    var czarnaEl = document.getElementById('cstatus-czarna-count');
    if (czarnaEl) czarnaEl.textContent = statusCounts.czarna_lista || '';

    var html = '';

    function _custAddrHtml(customer: CustomerRow) {
      var countryCode = (customer.delivery_country || '').toUpperCase();
      var flag = countryCode ? countryToFlag(countryCode) : '';
      var addrParts = [customer.delivery_street, customer.delivery_postal_code && customer.delivery_city ? customer.delivery_postal_code + ' ' + customer.delivery_city : (customer.delivery_city || customer.delivery_postal_code)].filter(Boolean);
      if (!addrParts.length) return '\u2014';
      return '<span style="display:inline-flex;align-items:center;gap:5px;flex-wrap:wrap">' +
        (flag ? flag : '') +
        '<span>' + escapeHtml(addrParts.join(', ')) + '</span>' +
      '</span>';
    }

    function _custTypeBadge(customer: CustomerRow) {
      var isCompany = customer.type === 'company';
      var typeIcon = isCompany ? 'building-2' : 'user';
      var typeLabel = isCompany ? 'Firma' : 'Osoba';
      var typeBadgeColor = isCompany ? '#3b82f6' : '#6b7280';
      return '<span style="display:inline-flex;align-items:center;gap:4px;background:' + typeBadgeColor + '22;color:' + typeBadgeColor + ';border:1px solid ' + typeBadgeColor + '44;font-size:10px;font-weight:600;padding:2px 7px;border-radius:9999px;white-space:nowrap">' +
        '<i data-lucide="' + typeIcon + '" style="width:10px;height:10px"></i>' + typeLabel +
      '</span>';
    }

    // Phase 1.2 (2026-04-29 perf-overhaul): server-side pagination — `state.customersList`
    // już zawiera tylko aktualnie załadowane strony (50 per page). NIE robimy slice() ani
    // groupCustomersByDupes() na liście — backend zwraca już paginowane wyniki.
    // Dupe-grouping wymagał pełnej listy (>5000 customers w jednym fetch'u = 3-5s);
    // teraz każdy customer jako osobny wiersz. Funkcjonalność scalania duplikatów
    // pozostaje w endpoint /api/customers/{id}/merge (uruchamiana z drawera, nie z listy).
    var _groupsPage = $customersList.get().map(function(c) { return [c]; });

    // Faza L.7.i — i18n status helper
    var i18nMod = window.__sbI18n;
    _groupsPage.forEach(function(grp) {
      var primary = grp[0];
      var dupeCount = grp.length - 1;
      var totalOrders = primary.orders || 0; // primary.orders już liczy wg email OR customer_id
      var statusObj = SellBaseData.getCustomerStatusObj(primary.status);
      // Translate status label using tStatus (fallback do statusObj.label dla custom)
      var statusLabel = i18nMod && i18nMod.tStatus ? i18nMod.tStatus('customer', statusObj.key, statusObj.label) : statusObj.label;
      var isCompany = primary.type === 'company';
      var displayName = isCompany ? primary.company_name : (primary.first_name + ' ' + primary.last_name);

      // ── Kontakt: expand z emailami duplikatów ────────────────────────
      var contactExtrasHtml = '';
      if (dupeCount > 0) {
        var extId = 'cext-' + primary.id.replace(/[^a-z0-9]/gi, '');
        var allDupIds = grp.slice(1).map(function(d) { return d.id; });
        var emailRows = grp.slice(1).map(function(dup) {
          return '<div style="display:flex;align-items:center;gap:6px;margin-top:3px">' +
            '<span style="font-size:0.78em;color:var(--color-text-faint);word-break:break-all">' + escapeHtml(dup.email || '\u2014') + '</span>' +
            '<button class="dup-merge-btn" data-primary-id="' + primary.id + '" data-merge-id="' + dup.id + '" style="flex-shrink:0;background:none;border:1px solid #f59e0b;color:#f59e0b;border-radius:5px;font-size:10px;font-weight:600;padding:1px 7px;cursor:pointer">Scal</button>' +
          '</div>';
        }).join('');
        contactExtrasHtml =
          '<div style="margin-top:5px">' +
            '<button class="cm-expand-btn" data-target="' + extId + '" data-open="0" style="background:var(--color-surface-2,rgba(255,255,255,.07));border:1px solid var(--color-border);border-radius:20px;color:var(--color-text-muted);font-size:10px;font-weight:500;cursor:pointer;padding:2px 9px;display:inline-flex;align-items:center;gap:4px">' +
              '<span class="ea" style="font-size:9px">\u25bc</span> +' + dupeCount + ' kontakty' +
            '</button>' +
            '<div id="' + extId + '" style="display:none;margin-top:6px;padding:8px;background:rgba(245,158,11,.06);border-radius:6px;border:1px solid rgba(245,158,11,.2)">' +
              emailRows +
              '<div style="margin-top:8px;text-align:right">' +
                '<button class="dup-merge-all-btn" data-primary-id="' + primary.id + '" data-merge-ids=\'' + JSON.stringify(allDupIds) + '\' style="background:#f59e0b;color:#fff;border:none;border-radius:5px;font-size:10px;font-weight:600;padding:3px 12px;cursor:pointer">Scal wszystkie</button>' +
              '</div>' +
            '</div>' +
          '</div>';
      }

      // ── Adres: expand z adresami innych rekordów grupy ──────────────
      var addrExtrasHtml = '';
      var otherAddrs: string[] = [];
      var primaryAddrKey = (primary.delivery_postal_code || '') + '|' + (primary.delivery_city || '');
      grp.slice(1).forEach(function(dup) {
        var key = (dup.delivery_postal_code || '') + '|' + (dup.delivery_city || '');
        if (key !== '|' && key !== primaryAddrKey) {
          var parts = [dup.delivery_street, dup.delivery_postal_code && dup.delivery_city ? dup.delivery_postal_code + ' ' + dup.delivery_city : (dup.delivery_city || dup.delivery_postal_code)].filter(Boolean);
          var cc = (dup.delivery_country || '').toUpperCase();
          var flag = cc ? countryToFlag(cc) + ' ' : '';
          otherAddrs.push(flag + escapeHtml(parts.join(', ')));
          primaryAddrKey = key; // dedupe consecutive same
        }
      });
      if (otherAddrs.length > 0) {
        var addrExtId = 'aext-' + primary.id.replace(/[^a-z0-9]/gi, '');
        addrExtrasHtml =
          '<div style="margin-top:5px">' +
            '<button class="cm-expand-btn" data-target="' + addrExtId + '" data-open="0" style="background:var(--color-surface-2,rgba(255,255,255,.07));border:1px solid var(--color-border);border-radius:20px;color:var(--color-text-muted);font-size:10px;font-weight:500;cursor:pointer;padding:2px 9px;display:inline-flex;align-items:center;gap:4px">' +
              '<span class="ea" style="font-size:9px">\u25bc</span> +' + otherAddrs.length + ' adres' + (otherAddrs.length > 1 ? 'y' : '') +
            '</button>' +
            '<div id="' + addrExtId + '" style="display:none;margin-top:6px;padding:8px;background:rgba(255,255,255,.03);border-radius:6px;border:1px solid var(--color-border)">' +
              otherAddrs.map(function(a) { return '<div style="font-size:0.8em;color:var(--color-text-muted);margin-top:2px">' + a + '</div>'; }).join('') +
            '</div>' +
          '</div>';
      }

      // ── Row ─────────────────────────────────────────────────────────
      html += '<tr style="cursor:pointer" data-customer-id="' + primary.id + '">' +
        '<td style="font-weight:500;color:var(--color-text-muted)">' + primary.id + '</td>' +
        '<td>' + _custTypeBadge(primary) +
          '<div style="margin-top:4px"><span class="status-badge" data-status-cat="customer" data-status-key="' + escapeAttr(statusObj.key || '') + '" style="background:' + statusObj.color + ';color:#fff;font-size:10px;padding:2px 7px;border-radius:9999px">' + escapeHtml(statusLabel) + '</span></div>' +
        '</td>' +
        '<td style="font-weight:500">' + escapeHtml(displayName) +
          (isCompany && primary.nip ? '<br><span style="font-size:0.8em;color:var(--color-text-muted)">NIP: ' + escapeHtml(primary.nip) + ' ' + _vatBadgeHtml(primary.nip_status) + '</span>' : '') +
          // 0238: chipy tagów pod nazwą; klik chipa filtruje listę po tym tagu.
          ((primary as any).tags && (primary as any).tags.length ? '<div style="margin-top:3px">' + chipyTagowKlienta((primary as any).tags, primary.id, { filtr: true }) + '</div>' : '') +
        '</td>' +
        '<td style="font-size:0.85em">' +
          (primary.phone ? '<div style="white-space:nowrap">' + escapeHtml(primary.phone) + '</div>' : '') +
          (primary.email ? '<div style="color:var(--color-text-muted)">' + escapeHtml(primary.email) + '</div>' : '') +
          contactExtrasHtml +
        '</td>' +
        '<td style="font-size:0.85em">' + _custAddrHtml(primary) + addrExtrasHtml + '</td>' +
        '<td style="text-align:center">' + totalOrders + '</td>' +
      '</tr>';
    });

    container.innerHTML = html || '<tr><td colspan="6" style="text-align:center;padding:32px;color:var(--color-text-muted)">Brak klientów spełniających kryteria</td></tr>';
    lucide.createIcons();
    // 0238: pasek tagów (filtr + „Tagi…") nad tabelą.
    try { pasekTagowKlientow(); } catch (_e) {}

    // 2026-06-02: numerowana paginacja (jak w zamówieniach) — zastępuje infinite-scroll.
    // Współdzielony komponent renderPagination: "Wyniki X–Y z N" + selektor per-page +
    // numery stron + prev/next. Klik strony → pagination._bindPaginationEvents ustawia
    // state.currentPage.customers i woła _rerenderView('customers') → renderCustomers().
    var custTotal = state.customersTotal || $customersList.get().length;
    var totalPages = Math.max(1, Math.ceil(custTotal / PAGE_SIZE));
    // Clamp gdyby strona wykraczała poza zakres (np. po zmianie per-page).
    if (currentPage > totalPages) {
      state.currentPage.customers = totalPages;
    }
    if (typeof renderPagination === 'function') {
      renderPagination('customers-pagination', Math.min(currentPage, totalPages), totalPages, custTotal, 'customers');
    }
    if (typeof lucide !== 'undefined') { try { lucide.createIcons(); } catch(_) {} }

    // Delegowany handler kliknięć
    container.addEventListener('click', function(e: any) {
      // Expand/collapse sekcji
      var expandBtn = e.target.closest('.cm-expand-btn');
      if (expandBtn) {
        e.preventDefault();
        e.stopPropagation();
        var targetId = expandBtn.dataset.target;
        var targetEl = document.getElementById(targetId);
        if (!targetEl) return;
        var isOpen = expandBtn.dataset.open === '1';
        targetEl.style.display = isOpen ? 'none' : '';
        expandBtn.dataset.open = isOpen ? '0' : '1';
        var arrow = expandBtn.querySelector('.ea');
        if (arrow) arrow.textContent = isOpen ? '\u25bc' : '\u25b2';
        return;
      }
      // Scal pojedynczy rekord
      var mergeBtn = e.target.closest('.dup-merge-btn');
      if (mergeBtn) {
        e.stopPropagation();
        var primaryId = mergeBtn.dataset.primaryId;
        var mergeId = mergeBtn.dataset.mergeId;
        mergeBtn.disabled = true;
        mergeBtn.textContent = '...';
        SellBaseAPI.mergeCustomers(primaryId, [mergeId]).then(function() {
          showToast('Klient scalony', 'success');
          odswiezKlientowPoMutacji();
        }).catch(function(err: ApiError) {
          showToast(err.message || 'Nie udało się scalić', 'error');
          mergeBtn.disabled = false;
          mergeBtn.textContent = 'Scal';
        });
        return;
      }
      // Scal wszystkie
      var mergeAllBtn = e.target.closest('.dup-merge-all-btn');
      if (mergeAllBtn) {
        e.stopPropagation();
        var pId = mergeAllBtn.dataset.primaryId;
        var mIds = JSON.parse(mergeAllBtn.dataset.mergeIds || '[]');
        mergeAllBtn.disabled = true;
        mergeAllBtn.textContent = 'Scalanie...';
        SellBaseAPI.mergeCustomers(pId, mIds).then(function() {
          showToast('Klienci scaleni', 'success');
          odswiezKlientowPoMutacji();
        }).catch(function(err: ApiError) {
          showToast(err.message || 'Nie udało się scalić', 'error');
          mergeAllBtn.disabled = false;
          mergeAllBtn.textContent = 'Scal wszystkie';
        });
        return;
      }
      // 0238: chip tagu w wierszu FILTRUJE (obsługa w delegacie dokumentowym
      // customer-tags.ts) — handler tbody odpala się w bublingu wcześniej,
      // więc bez tego guarda klik chipa otwierałby też drawer klienta.
      if (e.target.closest('.ctag-chip-filtr, .ctag-rzad')) return;
      // Klik w wiersz → drawer klienta
      var tr = e.target.closest('tr[data-customer-id]');
      if (tr) {
        openCustomerDrawer(tr.dataset.customerId);
      }
    });
}

// ── EXPOSE: extracted functions ──
(window as any).SellBaseApp = (window as any).SellBaseApp || {};
window.countryToFlag = countryToFlag; window.SellBaseApp.countryToFlag = countryToFlag;
window.groupCustomersByDupes = groupCustomersByDupes; window.SellBaseApp.groupCustomersByDupes = groupCustomersByDupes;
window.renderCustomers = renderCustomers; window.SellBaseApp.renderCustomers = renderCustomers;

// ── ES Module exports ──
export {
  countryToFlag,
  groupCustomersByDupes,
  odswiezKlientowPoMutacji,
  renderCustomers,
};
